import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import { toast } from "react-toastify";

export default function DebitNoteItems() {
  const { creditNoteId } = useParams();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!creditNoteId) return;
    setLoading(true);
    setError(null);

    axios
      .get(`${Base_URL}/CreditNote/getVendorInvoiceItems/${encodeURIComponent(creditNoteId)}`)
      .then((res) => {
        const normalized = (res.data || []).map((it) => {
          const qty = Number(it.invoiceQuantity) || 0;
          const price = Number(it.price) || 0;
          const creditNotePriceStr = (price || 0).toFixed(2);

          const cgstP = Number(it.cgst) || 0;
          const sgstP = Number(it.sgst) || 0;
          const igstP = Number(it.igst) || 0;

          const taxable = price * qty;
          const cgstAmt = taxable * (cgstP / 100);
          const sgstAmt = taxable * (sgstP / 100);
          const igstAmt = taxable * (igstP / 100);
          const gross = taxable + cgstAmt + sgstAmt + igstAmt;

          return {
            vendorInvoiceItemId: it.vendorInvoiceItemId,
            itemCode: it.itemCode,
            quantity: qty,
            price,
            creditNotePrice: creditNotePriceStr,
            cgst: cgstP,
            sgst: sgstP,
            igst: igstP,
            taxableValue: taxable,
            cgstAmount: cgstAmt,
            sgstAmount: sgstAmt,
            igstAmount: igstAmt,
            grossValue: gross,
            selected: false,
            calculateGst: true,
          };
        });
        setItems(normalized);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load invoice items");
      })
      .finally(() => setLoading(false));
  }, [creditNoteId]);

  function recalcItem(copy, i) {
    const it = copy[i];
    const qty = Number(it.quantity) || 0;
    const price = Number(it.creditNotePrice) || 0;

    const taxable = qty * price;
    const cgstAmt = it.calculateGst ? taxable * (it.cgst / 100) : 0;
    const sgstAmt = it.calculateGst ? taxable * (it.sgst / 100) : 0;
    const igstAmt = it.calculateGst ? taxable * (it.igst / 100) : 0;

    copy[i] = {
      ...it,
      taxableValue: taxable,
      cgstAmount: cgstAmt,
      sgstAmount: sgstAmt,
      igstAmount: igstAmt,
      grossValue: taxable + cgstAmt + sgstAmt + igstAmt,
    };
  }

  function changeCreditNotePrice(i, value) {
    setItems((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], creditNotePrice: (value || "").toString().replace(",", ".") };
      recalcItem(copy, i);
      return copy;
    });
  }

  function changeQuantity(i, value) {
    // allow only numeric values; empty string will be treated as 0
    const sanitized = value === "" ? "" : value.toString();
    setItems((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], quantity: sanitized === "" ? 0 : Number(sanitized) };

      recalcItem(copy, i);
      return copy;
    });
  }

  function toggleCalculateGst(i, checked) {
    setItems((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], calculateGst: typeof checked === "boolean" ? checked : !copy[i].calculateGst };
      recalcItem(copy, i);
      return copy;
    });
  }

  function toggleSelect(i, checked) {
    setItems((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], selected: typeof checked === "boolean" ? checked : !copy[i].selected };
      return copy;
    });
  }

  function handleSelectAll(e) {
    const checked = e.target.checked;
    setItems((prev) => prev.map((it) => ({ ...it, selected: checked })));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const selected = items
        .filter((x) => x.selected)
        .map((x) => ({
          vendorInvoiceItemId: x.vendorInvoiceItemId,
          itemCode: x.itemCode,
          quantity: x.quantity,
          originalPrice: x.price,
          creditNotePrice: Number(x.creditNotePrice) || 0,
          calculateGst: Boolean(x.calculateGst),
          taxableValue: x.taxableValue,
          cgstAmount: x.cgstAmount,
          sgstAmount: x.sgstAmount,
          igstAmount: x.igstAmount,
          grossValue: x.grossValue,
        }));

      if (selected.length === 0) {
        toast.info("No items selected to submit");
        setSubmitting(false);
        return;
      }

      const payload = {
        creditNoteId,
        organisationId: user?.organisationId,
        userId: user?.userId,
        items: selected,
      };

      const res = await axios.post(`${Base_URL}/CreditNote/postDebitNoteItems`, payload);
      if (res.status === 200) toast.success("Credit Note Items Submitted");
    } catch (err) {
      console.error(err);
      setError("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  const allSelected = items.length > 0 && items.every((it) => it.selected);

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white shadow rounded-2xl">
      <h2 className="text-2xl font-semibold mb-4">Credit Note Items — {creditNoteId}</h2>
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="text-left bg-gray-50">
                  <th className="p-2 border">
                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
                  </th>
                  <th className="p-2 border">Item Code</th>
                  <th className="p-2 border">Qty</th>
                  <th className="p-2 border">Original Price</th>
                  <th className="p-2 border">CreditNote Price</th>
                  <th className="p-2 border">GST</th>
                  <th className="p-2 border">Taxable</th>
                  <th className="p-2 border">CGST</th>
                  <th className="p-2 border">SGST</th>
                  <th className="p-2 border">IGST</th>
                  <th className="p-2 border">Gross</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.vendorInvoiceItemId}-${i}`} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2 border">
                      <input
                        type="checkbox"
                        checked={!!it.selected}
                        onChange={(e) => toggleSelect(i, e.target.checked)}
                      />
                    </td>
                    <td className="p-2 border">{it.itemCode}</td>
                    <td className="p-2 border">
                     <input
  type="number"
  min={0}
  step="0.01"   // allow decimals
  className="w-20 border rounded p-1"
  value={it.quantity}
  onChange={(e) => changeQuantity(i, e.target.value)}
/>
                    </td>
                    <td className="p-2 border">{it.price}</td>
                    <td className="p-2 border">
                      <input
                        className="w-24 border rounded p-1"
                        value={it.creditNotePrice}
                        onChange={(e) => changeCreditNotePrice(i, e.target.value)}
                      />
                    </td>
                    <td className="p-2 border text-center">
                      <input
                        type="checkbox"
                        checked={!!it.calculateGst}
                        onChange={(e) => toggleCalculateGst(i, e.target.checked)}
                      />
                    </td>
                    <td className="p-2 border">{Number(it.taxableValue || 0).toFixed(2)}</td>
                    <td className="p-2 border">{Number(it.cgstAmount || 0).toFixed(2)}</td>
                    <td className="p-2 border">{Number(it.sgstAmount || 0).toFixed(2)}</td>
                    <td className="p-2 border">{Number(it.igstAmount || 0).toFixed(2)}</td>
                    <td className="p-2 border">{Number(it.grossValue || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button type="submit" disabled={submitting} className="px-4 py-2 rounded bg-blue-600 text-white">
            {submitting ? "Submitting..." : "Submit Selected Items"}
          </button>
        </form>
      )}
    </div>
  );
}
